import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { SaaePreloaderCore } from '@components/SaaePreloaderCore';
import { STAGGER_INTERVAL, STAGGER_START_DELAY, LIST_ITEM_DURATION, easeSnappy } from '@utils/motionConfig';

export interface LoadingBlockProps {
  /** Optional custom label; defaults to common.loading */
  label?: string;
  /** Optional extra classes to control min-height, margins, etc. */
  className?: string;
}

export function LoadingBlock({ label, className = 'min-h-screen' }: LoadingBlockProps) {
  const { t } = useTranslation();
  const text = label ?? t('common.loading');

  return (
    <motion.div
      // Ensure the loading indicator centers horizontally as well,
      // even when the parent/container doesn't explicitly provide width.
      className={`flex w-full ${className} flex-col items-center justify-center gap-4 font-sans`}
      aria-busy="true"
      aria-live="polite"
      aria-label={text}
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: {
            staggerChildren: STAGGER_INTERVAL,
            delayChildren: STAGGER_START_DELAY,
          },
        },
        hidden: {},
      }}
    >
      <motion.div
        className="flex max-h-full max-w-full shrink-0 items-center justify-center"
        aria-hidden
        variants={{
          hidden: { opacity: 0, y: 8 },
          visible: { opacity: 1, y: 0, transition: { duration: LIST_ITEM_DURATION, ease: easeSnappy } },
        }}
      >
        <SaaePreloaderCore className="max-h-full min-h-0 min-w-0 max-w-full" />
      </motion.div>
      <motion.p
        variants={{
          hidden: { opacity: 0, y: 8 },
          visible: {
            opacity: 1,
            y: 0,
            transition: { duration: LIST_ITEM_DURATION, ease: easeSnappy },
          },
        }}
        className="sr-only"
      >
        {text}
      </motion.p>
    </motion.div>
  );
}
